import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GoogleMap from "@/components/GoogleMap";
import NearbySuburbs from "@/components/NearbySuburbs";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { locationConfigs } from "@/data/locationData";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
  CheckCircle,
  Heart,
  Shield,
  Star,
  Award,
  MapPin,
  Users
} from "lucide-react";

type LocationTemplateProps = {
  suburbName: string;
  suburbSlug: string;
  image?: string;
};

const LocationTemplate: React.FC<LocationTemplateProps> = ({ suburbName, suburbSlug, image = '/og-shopfront.jpg' }) => {
  useScrollAnimation();

  const config = locationConfigs[suburbSlug] ?? locationConfigs.broadmeadows;
  const isBroadmeadows = suburbName === "Broadmeadows";
  const locationPreposition = isBroadmeadows ? "serving" : "near";
  const locationPhrase = `${locationPreposition} ${suburbName}`;
  const seoTitle = `Dentist ${suburbName} | Y3 Smiles Dental — Family & Emergency`;
  const seoDescription = `Family dentist ${locationPhrase}. CDBS for kids, all health funds, same-day emergency. In nearby Broadmeadows. Book online.`;
  const canonicalUrl = `https://y3smilesdental.com.au/locations/${suburbSlug}/`;

  const services = [
    "Comprehensive dental check-ups and hygiene",
    "Family dentistry for children, teens, and adults",
    "Cosmetic dentistry: whitening, veneers, smile makeovers",
    "Restorative care: fillings, crowns, and bridges",
    "Emergency and same-day pain relief appointments",
    "Gentle care for anxious patients"
  ];

  const whyChooseUs = [
    {
      icon: Users,
      title: "Personalised Care",
      description: "We listen carefully and tailor every treatment plan to your needs."
    },
    {
      icon: Heart,
      title: "Calm & Gentle",
      description: "A relaxed environment and gentle techniques for a stress-free visit."
    },
    {
      icon: Shield,
      title: "Modern Dentistry",
      description: "Up-to-date technology and evidence-based care for long-term results."
    },
    {
      icon: Award,
      title: "Trusted Local Team",
      description: `Experienced clinicians ${locationPhrase} and surrounding suburbs.`
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title={seoTitle} description={seoDescription} canonicalUrl={canonicalUrl} image={image} />
      <Header />
      <main className="flex-grow">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-28 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary"></div>
            <div className="absolute top-40 right-20 w-20 h-20 rounded-full bg-secondary"></div>
            <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-primary"></div>
            <div className="absolute bottom-32 right-1/3 w-24 h-24 rounded-full bg-secondary"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-flex items-center justify-center mb-6 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft">
                <img src="/location.png" alt="Location" className="w-10 h-10 mr-4" />
                <div className="text-left">
                  <p className="text-sm font-medium text-primary uppercase tracking-wider">Dentist {locationPhrase}</p>
                  <p className="text-lg font-bold text-neutral-800">{suburbName}</p>
                </div>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-neutral-800 mb-6 leading-tight">
                Dentist {locationPreposition}
                <span className="block text-primary">{suburbName}</span>
              </h1>

              <p className="text-xl text-neutral-800 mb-10 leading-relaxed max-w-3xl mx-auto">
                Your local dentist for check-ups, restorative care, and cosmetic dentistry. We provide modern, gentle
                care in a welcoming, family-friendly clinic.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft">
                  <CheckCircle className="w-6 h-6 text-primary mr-3" />
                  <span className="font-semibold text-neutral-800">Local Dental Care</span>
                </div>
                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft">
                  <Award className="w-6 h-6 text-primary mr-3" />
                  <span className="font-semibold text-neutral-800">Experienced Team</span>
                </div>
                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft">
                  <Heart className="w-6 h-6 text-primary mr-3" />
                  <span className="font-semibold text-neutral-800">Family Friendly</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button variant="booking" size="xl" asChild className="group">
                  <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                    <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                    BOOK APPOINTMENT
                  </a>
                </Button>
                <Button variant="booking" size="xl" asChild>
                  <Link to="/treatments">View Our Treatments</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* About */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                  Local Dental Care
                </h2>
                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                  Trusted dentist {locationPhrase}
                </h2>
                <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                  Our Broadmeadows clinic serves patients from {suburbName} and surrounding suburbs with a full
                  range of dental services. We prioritise comfort, clarity, and long-term oral health at every visit.
                </p>
                <div className="flex items-start gap-3 text-neutral-800">
                  <MapPin className="w-6 h-6 text-primary mt-1" />
                  <p>
                    Conveniently located in Broadmeadows with easy parking and public transport access.
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-8 rounded-3xl">
                <h3 className="text-2xl font-bold text-neutral-800 mb-4">What you can expect</h3>
                <ul className="space-y-3">
                  {services.map((service, index) => (
                    <li key={index} className="flex items-center text-neutral-800">
                      <CheckCircle className="w-5 h-5 text-primary mr-3" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-24 bg-neutral-50">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">
                Dental services in {suburbName}
              </h2>
              <p className="text-lg text-neutral-800">
                From routine check-ups to cosmetic and emergency care, we provide comprehensive dentistry for all ages.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-6 rounded-2xl shadow-soft border border-primary/5">
                  <div className="flex items-center mb-3">
                    <Star className="w-5 h-5 text-primary mr-2" />
                    <h3 className="text-lg font-semibold text-neutral-800">{service}</h3>
                  </div>
                  <p className="text-neutral-800 text-sm">
                    Our team provides this service with a gentle, patient-first approach tailored to your needs.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">
                Why {suburbName} families choose Y3 Smiles Dental
              </h2>
              <p className="text-lg text-neutral-800">
                A caring team, modern dentistry, and a focus on long-term oral health.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {whyChooseUs.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="bg-neutral-50 p-8 rounded-2xl border border-primary/10">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-800 mb-2">{item.title}</h3>
                    <p className="text-neutral-800">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-neutral-50 to-primary/5">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold text-neutral-800 mb-6">Book your dentist {locationPhrase}</h2>
              <p className="text-lg text-neutral-800 mb-8">
                Ready to look after your smile? Book your appointment today and experience gentle, modern care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="booking" size="xl" asChild className="group">
                  <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                    <Heart className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                    BOOK APPOINTMENT
                  </a>
                </Button>
                <Button variant="outline" size="xl" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <NearbySuburbs suburbSlug={suburbSlug} />

        <GoogleMap
          locationName={suburbName}
          locationData={config.locationData}
          openingHours={config.openingHours}
          className="bg-primary/5"
        />
      </main>
      <Footer />
    </div>
  );
};

export default LocationTemplate;